
import React, { useState, useRef } from 'react';
import { Camera, Upload, X, Loader2, CheckCircle, AlertCircle, RefreshCw, Layers } from 'lucide-react';
import { ProcessingStatus } from './types';

// interface ScannerProps {
//   exam: ExamTemplate;
//   onResult: (results: { 
//     answers: AnswerKey[], 
//     studentRoll?: string, 
//     imageUrl: string, 
//     confidence?: number,
//     score?: number,
//     maxMarks?: number,
//     correct?: number,
//     wrong?: number,
//     blank?: number,
//     multiple?: number,
//     report?: BackendReportEntry[],
//     annotatedImage?: string
//   }) => void;
//   onCancel: () => void;
// }

const Scanner = ({ studentId, exam, onResult, onCancel }) => {
 const [status, setStatus] = useState({
  isProcessing: false,
  step: 'Idle',
  progress: 0,
});


const [selectedFile, setSelectedFile] = useState(null);

const fileInputRef = useRef(null);  const [preview, setPreview] = useState(null);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };


  const startAnalysis = async () => {
    if (!selectedFile) return;

    setStatus({ isProcessing: true, step: 'Uploading', progress: 10 });
    
    try {
      const formData = new FormData();
      formData.append('omr_image', selectedFile);
      formData.append('auto_detection', 'true');
      formData.append('threshold_method', 'auto');
      formData.append('include_annotated', 'true');
      // Send current exam metadata to the Python scanner API.
      // This keeps grading in sync with the exam created inside the React app.
      formData.append('total_questions', String(exam.total_questions));
      formData.append('options_count', String(5));
      formData.append('marks_correct', String(exam.correct_marks));
      formData.append('marks_wrong', String(exam.wrong_marks));
      formData.append('blank_marks', String(exam.penalty));
      formData.append('multiple_marks', String(exam.marksWrong));
      formData.append('answer_key',  JSON.stringify(
    exam.questions.map(q => ({
      question: q.question_no,
      answer: q.answer
    }))
  ));

      formData.append('student_id', String(studentId));
// console.log(process.env.REACT_APP_UPLOAD_API);
      setStatus({ isProcessing: true, step: 'Analyzing', progress: 40 });
      const response = await fetch(`${process.env.REACT_APP_UPLOAD_API}/scan`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        let errorMessage = `Server responded with ${response.status}`;
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errData = await response.json();
            errorMessage = errData.error || errorMessage;
          } else {
            const text = await response.text();
            // If it's a long HTML error page, don't show the whole thing
            if (text.includes('<!DOCTYPE html>') || text.includes('<html>')) {
              errorMessage = `Server Error (${response.status}): The server returned an HTML error page instead of JSON.`;
            } else {
              errorMessage = text.slice(0, 100) || errorMessage;
            }
          }
        } catch (e) {
          // Fallback to default message
        }
        throw new Error(errorMessage);
      }

      setStatus({ isProcessing: true, step: 'Finalizing', progress: 90 });

      let data;
      try {
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          const text = await response.text();
          console.error("Expected JSON but got:", text.slice(0, 200));
          throw new Error("The server did not return a valid JSON response.");
        }
        data = await response.json();
      } catch (jsonErr) {
        throw new Error(`Failed to parse server response: ${jsonErr.message}`);
      }

setStatus({
  isProcessing: false,
  step: 'Done',
  progress: 100
});

      let rawReport= [];
      if (Array.isArray(data.report)) {
        rawReport = data.report;
      } else if (data.report && typeof data.report === 'object') {
        rawReport = Object.entries(data.report).map(([key, value]) => {
          if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            return { Question: parseInt(key) || key, ...value };
          } else if (Array.isArray(value)) {
            return { Question: parseInt(key) || key, "Student Answer": value[0] || 'None' };
          }
          return { Question: parseInt(key) || key, "Student Answer": value };
        });
      }
      // Ensure the report sent to onResult has consistent keys, following user's requested backend format
const safeReport = rawReport.map((item, idx) => {
  const q_no =
    item.Question ??
    item.q_no ??
    item.question_no ??
    idx + 1;

  const marked =
    item["Student Answer"] ??
    item.marked ??
    item.answer ??
    "-";

  const correct =
    item["Correct Answer"] ??
    item.correct ??
    exam.questions?.find(q => q.question_no == q_no)?.answer ??
    "N/A";

  const status =
    marked === "-" || !marked
      ? "Blank"
      : marked.toUpperCase() === String(correct).toUpperCase()
        ? "Correct"
        : "Wrong";

  const marks =
    status === "Correct"
      ? Number(exam.correct_marks || 1)
      : status === "Wrong"
        ? Number(exam.wrong_marks || -1)
        : 0;

  return {
    q_no,
    marked,
    correct,
    status,
    marks
  };
});



      const totalScore = safeReport.reduce((acc, curr) => acc + curr.marks, 0);
      const totalCorrect = safeReport.filter(r => r.status == 'Correct').length;
      const totalWrong = safeReport.filter(r => r.status == 'Wrong').length;
      const totalBlank = safeReport.filter(r => r.status == 'Blank').length;
      onResult({
          examData: exam,
        studentRoll: data.student_id || data.roll_number || data.student_roll || data.roll || undefined,
        imageUrl: preview || '',
        score: totalScore,
        maxMarks: data.maximum_marks || data.max_marks || (exam.total_questions * exam.marksCorrect),
        correct: totalCorrect,
        wrong: totalWrong,
        blank: totalBlank,
        multiple: data.multiple,
        report: safeReport,
        annotatedImage: data.annotated_image?.base64 ? `data:image/jpeg;base64,${data.annotated_image.base64}` : undefined
      });
      
    } catch (err) {
      console.error("Analysis error:", err);
      setStatus({ isProcessing: false, step: 'Idle', progress: 0, error: err.message || "Scanning failed." });
    }
  };

  return (
<div
  className="position-fixed top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center p-3"
  style={{
    zIndex: 100,
    backgroundColor: "rgba(0,0,0,0.9)"
  }}
>
    <div
  className="bg-white rounded shadow-lg d-flex flex-column"
  style={{
    width: "100%",
    maxWidth: "800px",
    maxHeight: "90vh",
    overflow: "hidden"
  }}
>

<div className="p-4 border-bottom bg-light d-flex justify-content-between align-items-center">
              <div>
            <p className="fs-4 my-0 fw-bold">
              <Layers className="text-primary" /> OMR Scanner
            </p>
            <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">{exam.title}</p>
          </div>
          <button onClick={onCancel} className="bg-light border-0">
            <X className="text-secondary" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-8 flex flex-col items-center justify-center min-h-[400px]">
          {status.isProcessing ? (
            <div className="text-center space-y-6">
              <div className="relative">
           <div
  className="rounded-circle border border-4 d-flex align-items-center justify-content-center mx-auto"
  style={{
    width: "96px",
    height: "96px"
  }}
>         <Loader2 className="w-12 h-12 text-indigo-600 animate-spin" />
                </div>
                <div className="absolute inset-0 bg-indigo-500/10 rounded-full animate-ping scale-150 opacity-20"></div>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-1">{status.step}...</h3>
              </div>
            <div className="progress mx-auto" style={{ width: "250px" }}>
  <div
    className="progress-bar bg-primary"
    role="progressbar"
    style={{ width: `${status.progress}%` }}
  >
  </div>
</div>
            </div>
          ) : preview ? (
            <div className="w-full space-y-6 flex flex-col text-center">
        <img src={preview} alt="OMR Preview" style={{height: '20em'}} />
         <br />      
         
                       {status.error && (
            <div className="alert alert-danger d-flex align-items-center mx-4 p-2 mt-2">     
              <AlertCircle className="me-2" />
                    <span className="text-sm font-semibold">{status.error}</span>
                </div>
              )}
              
               <button 
                    onClick={() => setPreview(null)}
                    className="btn btn-secondary p-2 fw-bold mt-3 me-2"
                >
                    <RefreshCw className="me-2" />Re Upload
                </button>
              


                <button 
                  onClick={startAnalysis}
  className="btn btn-primary mt-3 p-2 fw-bold"
>
                  <CheckCircle className="me-2" />
                  Start Analysis
                </button>
            </div>
          ) : (
            <div 
              onClick={() => fileInputRef.current?.click()}
  className="border border-3 rounded d-flex flex-column align-items-center justify-content-center text-center"
  style={{
    height: "320px",
    cursor: "pointer",
    borderStyle: "dashed"
  }}
>
                <Camera className="w-25 h-25" />

              <p className="fs-4 my-0">Upload Answer Sheet</p>
              <p className="text-muted">
               Click here & upload a clear image of the completed OMR answer sheet to begin analysis.
              </p>
              <input type="file" ref={fileInputRef} className="d-none" accept="image/*" onChange={handleFile} />
            </div>
          )}
        </div>

       <div className="p-3 bg-light border-top text-center">
</div>
      </div>
    </div>
  );
};

export default Scanner;
